import {
  useMemo,
  useState,
} from "react";

import {
  supabase,
} from "../../lib/supabaseClient";

// LOT0F1_PRP_SERVER_COORDINATE_VALIDATION_QUEUE_UI_V1

type AnyRecord =
  Record<string, unknown>;

const QUEBEC_REGIONS = [
  "Abitibi-Témiscamingue",
  "Bas-Saint-Laurent",
  "Capitale-Nationale",
  "Centre-du-Québec",
  "Chaudière-Appalaches",
  "Côte-Nord",
  "Estrie",
  "Gaspésie–Îles-de-la-Madeleine",
  "Lanaudière",
  "Laurentides",
  "Laval",
  "Mauricie",
  "Montérégie",
  "Montréal",
  "Nord-du-Québec",
  "Outaouais",
  "Saguenay–Lac-Saint-Jean",
];

const asText = (
  value: unknown,
  fallback = "—",
): string =>
  typeof value === "string" && value.trim()
    ? value.trim()
    : fallback;

const asNumber = (
  value: unknown,
): number => {
  const parsed =
    Number(value);

  return Number.isFinite(parsed)
    ? parsed
    : 0;
};

export default function PrpAdminTerritoryCoordinateValidationPanel() {
  const [
    prpEmail,
    setPrpEmail,
  ] = useState("testprp@forfeo.com");

  const [
    region,
    setRegion,
  ] = useState("Capitale-Nationale");

  const [
    result,
    setResult,
  ] = useState<AnyRecord | null>(null);

  const [
    busyAction,
    setBusyAction,
  ] = useState<string | null>(null);

  const items =
    useMemo(
      () =>
        Array.isArray(result?.items)
          ? result.items as AnyRecord[]
          : [],
      [
        result,
      ],
    );

  const counters =
    result?.counters && typeof result.counters === "object"
      ? result.counters as AnyRecord
      : {};

  const callEdge =
    async (
      action: string,
      extraBody: AnyRecord = {},
    ) => {
      setBusyAction(action);
      setResult(null);

      try {
        const {
          data: sessionData,
        } =
          await supabase.auth.getSession();

        const token =
          sessionData.session?.access_token;

        if (!token) {
          setResult({
            ok:
              false,
            error:
              "missing_session_token",
          });

          setBusyAction(null);
          return;
        }

        const supabaseUrl =
          (import.meta as any).env?.VITE_SUPABASE_URL as string | undefined;

        if (!supabaseUrl) {
          setResult({
            ok:
              false,
            error:
              "missing_vite_supabase_url",
          });

          setBusyAction(null);
          return;
        }

        const response =
          await fetch(
            `${supabaseUrl}/functions/v1/prp-coordinate-validation`,
            {
              method:
                "POST",
              headers: {
                Authorization:
                  `Bearer ${token}`,
                "Content-Type":
                  "application/json",
              },
              body:
                JSON.stringify({
                  action,
                  prp_email:
                    prpEmail.trim(),
                  region,
                  ...extraBody,
                }),
            },
          );

        const raw =
          await response.text();

        let parsed: AnyRecord;

        try {
          parsed =
            raw
              ? JSON.parse(raw)
              : {};
        } catch {
          parsed = {
            raw,
          };
        }

        setResult({
          ok:
            response.ok && parsed.ok !== false,
          http_status:
            response.status,
          ...parsed,
        });
      } catch (error) {
        setResult({
          ok:
            false,
          error:
            error instanceof Error
              ? error.message
              : "coordinate_validation_request_failed",
        });
      }

      setBusyAction(null);
    };

  return (
    <section
      className="mt-6 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm"
      data-lot0f1-prp-server-coordinate-validation-queue="true"
    >
      <div className="flex flex-col gap-2">
        <p className="text-xs font-black uppercase tracking-[0.28em] text-emerald-700">
          0F-1 · Validation serveur
        </p>

        <h3 className="text-lg font-black text-slate-950">
          File de validation des coordonnées côté serveur
        </h3>

        <p className="text-sm font-semibold text-slate-600">
          L’admin voit les commerces rattachés au PRP et peut valider seulement une position déjà présente côté serveur.
          Aucun champ manuel et aucun géocodage navigateur ne sont utilisés.
        </p>
      </div>

      <div className="mt-5 grid gap-3 lg:grid-cols-[1fr_1fr_auto]">
        <label className="flex flex-col gap-1 text-sm font-bold text-slate-700">
          Courriel PRP
          <input
            className="rounded-2xl border border-slate-200 px-4 py-3 text-sm font-bold text-slate-900 outline-none focus:border-emerald-500"
            value={prpEmail}
            onChange={(event) => setPrpEmail(event.target.value)}
            placeholder="testprp@forfeo.com"
          />
        </label>

        <label className="flex flex-col gap-1 text-sm font-bold text-slate-700">
          Région administrative
          <select
            className="rounded-2xl border border-slate-200 px-4 py-3 text-sm font-bold text-slate-900 outline-none focus:border-emerald-500"
            value={region}
            onChange={(event) => setRegion(event.target.value)}
          >
            {QUEBEC_REGIONS.map((item) => (
              <option
                key={item}
                value={item}
              >
                {item}
              </option>
            ))}
          </select>
        </label>

        <button
          className="rounded-2xl bg-slate-950 px-5 py-3 text-sm font-black text-white disabled:opacity-50 lg:self-end"
          disabled={Boolean(busyAction)}
          onClick={() => callEdge("preview_coordinate_validation_queue")}
          type="button"
        >
          {busyAction === "preview_coordinate_validation_queue"
            ? "Lecture…"
            : "Lire la file"}
        </button>
      </div>

      {result && (
        <div className="mt-5 rounded-2xl border border-slate-100 bg-slate-50 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded-full px-3 py-1 text-xs font-black ${
              result.ok
                ? "bg-emerald-100 text-emerald-800"
                : "bg-rose-100 text-rose-800"
            }`}>
              {result.ok ? "OK" : "À traiter"}
            </span>

            <span className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">
              {asText(result.action, "lecture")}
            </span>

            {result.error && (
              <span className="text-sm font-bold text-rose-700">
                {asText(result.error)}
              </span>
            )}
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <div className="rounded-2xl bg-white p-3">
              <p className="text-xs font-black uppercase text-slate-500">
                Assignés
              </p>
              <p className="text-2xl font-black text-slate-950">
                {asNumber(counters.total_assigned)}
              </p>
            </div>

            <div className="rounded-2xl bg-white p-3">
              <p className="text-xs font-black uppercase text-slate-500">
                Prêts à valider
              </p>
              <p className="text-2xl font-black text-slate-950">
                {asNumber(counters.ready_to_verify_existing_server_position)}
              </p>
            </div>

            <div className="rounded-2xl bg-white p-3">
              <p className="text-xs font-black uppercase text-slate-500">
                Enrichissement requis
              </p>
              <p className="text-2xl font-black text-slate-950">
                {asNumber(counters.server_enrichment_required)}
              </p>
            </div>
          </div>

          {items.length > 0 && (
            <div className="mt-4 space-y-3">
              {items.map((item) => (
                <div
                  className="rounded-2xl border border-slate-100 bg-white p-4"
                  key={asText(item.id)}
                >
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                      <p className="font-black text-slate-950">
                        {asText(item.name)}
                      </p>

                      <p className="text-sm font-bold text-slate-500">
                        {asText(item.category)} · {asText(item.city)} · {asText(item.status)}
                      </p>

                      <p className="mt-1 text-xs font-black uppercase tracking-[0.18em] text-slate-400">
                        {asText(item.readiness)}
                      </p>
                    </div>

                    <button
                      className="rounded-2xl bg-emerald-700 px-4 py-3 text-sm font-black text-white disabled:cursor-not-allowed disabled:bg-slate-300"
                      disabled={
                        Boolean(busyAction)
                        || item.readiness !== "ready_to_verify_existing_server_position"
                      }
                      onClick={() =>
                        callEdge(
                          "verify_existing_business_coordinates",
                          {
                            business_id:
                              item.id,
                          },
                        )
                      }
                      type="button"
                    >
                      {item.readiness === "ready_to_verify_existing_server_position"
                        ? "Valider côté serveur"
                        : item.readiness === "already_verified"
                          ? "Déjà validé"
                          : "Enrichissement requis"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <details className="mt-4">
            <summary className="cursor-pointer text-xs font-black uppercase tracking-[0.18em] text-slate-500">
              Réponse technique
            </summary>

            <pre className="mt-3 max-h-80 overflow-auto rounded-2xl bg-slate-950 p-4 text-xs font-bold text-slate-100">
              {JSON.stringify(result, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </section>
  );
}
